import { useEffect, useState } from "react";
import type { PopupItem } from "@/lib/types";
import { mediaUrl } from "@/lib/media";

interface PopupModalProps {
  popups: PopupItem[];
}

const STORAGE_KEY = "mindeulle_popup_hidden_until";

export default function PopupModal({ popups }: PopupModalProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!popups || popups.length === 0) return;
    const hiddenUntil = localStorage.getItem(STORAGE_KEY);
    if (hiddenUntil) {
      const until = parseInt(hiddenUntil, 10);
      if (!Number.isNaN(until) && Date.now() < until) return;
    }
    // 한 번에 첫 번째 팝업만 표시
    setVisible(true);
  }, [popups]);

  if (!popups || popups.length === 0 || !visible) return null;

  const popup = popups[0];
  const title = popup.subject || popup.title || "";
  const image = popup.thumb || popup.image || "";

  const close = () => setVisible(false);

  const hideToday = () => {
    const until = new Date();
    until.setHours(23, 59, 59, 999);
    localStorage.setItem(STORAGE_KEY, String(until.getTime()));
    setVisible(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-primary-950/60"
        onClick={close}
        aria-hidden="true"
      ></div>

      <div className="relative z-10 w-full max-w-md overflow-hidden rounded-xl bg-background-50 shadow-xl">
        {image && (
          <div className="relative h-48 w-full">
            <img
              src={mediaUrl(image)}
              alt={title || "공지 팝업"}
              className="h-full w-full object-cover object-top"
            />
          </div>
        )}
        <button
          type="button"
          onClick={close}
          aria-label="팝업 닫기"
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-primary-950/50 text-background-50 hover:bg-primary-950/70"
        >
          <i className="ri-close-line text-lg" aria-hidden="true"></i>
        </button>

        <div className="p-5">
          {title && (
            <h3 className="font-heading text-lg font-bold text-foreground-950">{title}</h3>
          )}
          {popup.content && (
            <p className="mt-2 text-sm text-foreground-700 whitespace-pre-line">{popup.content}</p>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-background-200 px-5 py-3 text-sm">
          <button
            type="button"
            onClick={hideToday}
            className="text-foreground-500 hover:text-foreground-700"
          >
            오늘 하루 보지 않기
          </button>
          <button
            type="button"
            onClick={close}
            className="rounded-full bg-primary-500 px-5 py-2 font-semibold text-background-50 hover:bg-primary-600"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}